import React from 'react';

import { formatExact, formatValue } from '../format';
import type { Palette } from '../palette';
import type { Envelope, EnvelopeColumn } from '../types';

type SplitBreakdownProps = { envelope: Envelope; palette: Palette; stacked?: boolean };


export default function SplitBreakdown({ envelope, palette, stacked = false }: SplitBreakdownProps) {
  const measure = envelope.columns.find((column) => column.role === 'measure') as EnvelopeColumn;
  const [outer, inner] = envelope.columns.filter((column) => column.role === 'dim');
  const value = (row: Envelope['rows'][number]) => (row[measure.name] as number | null) ?? 0;
  const leaves = envelope.rows.filter((row) => row[outer.name] !== undefined && row[inner.name] !== undefined);
  const innerTotals = new Map<string, number>();
  for (const row of leaves) innerTotals.set(String(row[inner.name]), (innerTotals.get(String(row[inner.name])) ?? 0) + value(row));
  const shown = [...innerTotals].sort((a, b) => b[1] - a[1]).map(([member]) => member);
  const colorOf = (member: string) => (shown.includes(member) ? palette.series[shown.indexOf(member) % palette.series.length] : palette.muted);
  const groups = new Map<string, Array<{ member: string; value: number }>>();
  for (const row of leaves) {
    const key = String(row[outer.name]);
    const member = shown.includes(String(row[inner.name])) ? String(row[inner.name]) : 'Other';
    const parts = groups.get(key) ?? [];
    const existing = parts.find((part) => part.member === member);
    if (existing) existing.value += value(row);
    else parts.push({ member, value: value(row) });
    groups.set(key, parts);
  }
  const ranked = [...groups].map(([key, parts]) => ({ key, parts: parts.sort((a, b) => shown.indexOf(a.member) - shown.indexOf(b.member)), total: parts.reduce((sum, part) => sum + part.value, 0) })).sort((a, b) => b.total - a.total);
  const largest = Math.max(...ranked.map((group) => Math.abs(group.total)), 0) || 1;
  const others = innerTotals.size > shown.length;
  if (!stacked || measure.additive === false || measure.format === 'pct') {
    const largestPart = Math.max(...leaves.map((row) => Math.abs(value(row))), 0) || 1;
    const outerKeys = [...new Set(leaves.map((row) => String(row[outer.name])))];
    const innerKeys = [...new Set(leaves.map((row) => String(row[inner.name])))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const cell = new Map(leaves.map((row) => [`${String(row[outer.name])}›${String(row[inner.name])}`, row]));
    return (
      <div className="tbl matrix">
        <table>
          <thead>
            <tr>
              <th className="dim">{outer.label}</th>
              {innerKeys.map((member) => (
                <th key={member}>{member}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {outerKeys.map((key) => (
              <tr key={key}>
                <td>{key}</td>
                {innerKeys.map((member) => {
                  const row = cell.get(`${key}›${member}`);
                  const share = row ? Math.abs(value(row)) / largestPart : 0;
                  return (
                    <td data-lens-ref={row ? `${envelope.query}.${measure.name}[${key}›${member}]` : undefined} key={member} style={{ '--part': `${share * 100}%` } as React.CSSProperties} title={row ? formatExact(value(row), measure.format) : undefined}>
                      {row ? formatValue(value(row), measure.format) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <>
      <div className="legend">
        {[...shown, ...(others ? ['Other'] : [])].map((member) => (
          <span key={member}>
            <i className="sw" style={{ background: colorOf(member) }} />
            {member}
          </span>
        ))}
      </div>
      <div className="rank" role="list">
        {ranked.map((group) => (
          <div className="r" key={group.key} role="listitem">
            <span className="k" title={group.key}>
              {group.key}
            </span>
            <span className="bar stack">
              {group.parts.map((part) => (
                <i key={part.member} style={{ width: `${(Math.abs(part.value) / largest) * 100}%`, background: colorOf(part.member) }} title={`${part.member}: ${formatValue(part.value, measure.format)}`} />
              ))}
            </span>
            <span className="val" data-lens-ref={`${envelope.query}.${measure.name}[${group.key}]`} title={formatExact(group.total, measure.format)}>
              {formatValue(group.total, measure.format)}
            </span>
            <span className="share" />
            <span className="dl" />
          </div>
        ))}
      </div>
    </>
  );
}
